import Chart from "react-apexcharts";
import { normalizeLiveTimeframe } from "../../../redux/live-suggestion/liveSuggestionSlice";
import { stockDisplayName } from "../../../shared/stockBoards";
import { Table } from "antd";
import styles from "../../../styles/shared/ReportBoard.module.css";

const ACTION_COLORS = ["#22a06b", "#d64545", "#5b6b8c"];
const BAR_COLORS = ["#2a86ff", "#0f1c3f", "#5b6b8c", "#22a06b", "#d64545"];
const STRENGTH_LABELS = {
  1: "Faint",
  2: "Feeble",
  3: "Fair",
  4: "Robust",
  5: "Powerful",
};

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function sideOf(value) {
  const side = String(value || "").split("/")[0];
  return ["Buy", "Sell", "Hold"].includes(side) ? side : "";
}

function activeSide(value) {
  const side = sideOf(value);
  return side === "Buy" || side === "Sell" ? side : "";
}

function realDisagree(board, live) {
  return Boolean(board && live && board !== live);
}

const TOP_COINS = 8;
const TOP_PIVOTS = 6;
const STRENGTH_RANK = { Faint: 1, Feeble: 2, Fair: 3, Robust: 4, Powerful: 5 };

function coinKey(value) {
  return String(value || "").split("/")[0].toUpperCase();
}

function strengthRank(value) {
  if (STRENGTH_RANK[value]) return STRENGTH_RANK[value];
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.max(1, Math.min(5, Math.round(n)));
}

function blankCoin(coin) {
  return {
    coin,
    buy: false,
    sell: false,
    buyCount: 0,
    sellCount: 0,
    holdCount: 0,
    live: "",
    liveLabel: "",
    strengthRank: 0,
    pivot: "off",
  };
}

function noteLive(coin, row) {
  const live = activeSide(row.live_side);
  if (!live) return;
  coin.live = live;
  coin.liveLabel = row.live_label || live;
}

function noteStrength(coin, value) {
  const rank = strengthRank(value);
  if (rank > coin.strengthRank) coin.strengthRank = rank;
}

function notePivot(coin, row) {
  if (row.pivot === "on" || row.pivot_detected) coin.pivot = "on";
}

function noteBoard(coin, suggestion) {
  const side = sideOf(suggestion);
  if (side === "Buy") {
    coin.buy = true;
    coin.buyCount += 1;
  } else if (side === "Sell") {
    coin.sell = true;
    coin.sellCount += 1;
  } else if (side === "Hold") {
    coin.holdCount += 1;
  }
}

function coinsFromVisual(visual) {
  const symbols = visual?.symbols;
  if (Array.isArray(symbols) && symbols.length) {
    return symbols.map((row) => {
      const coin = blankCoin(coinKey(row.symbol));
      noteBoard(coin, row.action);
      noteLive(coin, row);
      noteStrength(coin, row.strength_label || row.strength);
      notePivot(coin, row);
      return coin;
    }).filter((coin) => coin.coin);
  }
  const byCoin = {};
  (visual?.rows || []).forEach((row) => {
    const key = coinKey(row.coin);
    if (!key) return;
    if (!byCoin[key]) byCoin[key] = blankCoin(key);
    const coin = byCoin[key];
    noteBoard(coin, row.suggestion);
    noteLive(coin, row);
    noteStrength(coin, row.strength);
    notePivot(coin, row);
  });
  return Object.values(byCoin);
}

function boardSide(coin) {
  const buy = coin.buyCount || 0;
  const sell = coin.sellCount || 0;
  const hold = coin.holdCount || 0;
  if (buy > sell && buy > hold) return "Buy";
  if (sell > buy && sell > hold) return "Sell";
  if (buy > 0 && buy === sell && buy >= hold) return "Buy/Sell";
  if (buy || sell || hold) return "Hold";
  return "";
}

function singleBoard(coin) {
  const side = boardSide(coin);
  return side === "Buy" || side === "Sell" ? side : "";
}

function coinScore(coin) {
  const board = singleBoard(coin);
  let score = coin.strengthRank;
  if (coin.pivot === "on") score += 10;
  if (board) score += 100;
  if (coin.live) score += 1000;
  if (realDisagree(board, coin.live)) score += 10000;
  return score;
}

function isUsefulCoin(coin) {
  const counted = (coin.buyCount || 0) + (coin.sellCount || 0) + (coin.holdCount || 0);
  return Boolean(counted || coin.live || coin.pivot === "on");
}

export function boardCoinMetrics(visualSummary) {
  const coins = coinsFromVisual(visualSummary);
  let liveBuy = 0;
  let liveSell = 0;
  let pivots = 0;
  let conflicts = 0;
  coins.forEach((coin) => {
    if (coin.live === "Buy") liveBuy += 1;
    if (coin.live === "Sell") liveSell += 1;
    if (coin.pivot === "on") pivots += 1;
    if (realDisagree(singleBoard(coin), coin.live)) conflicts += 1;
  });
  return { liveBuy, liveSell, pivots, conflicts };
}

function rankedCoins(visualSummary) {
  return coinsFromVisual(visualSummary)
    .filter(isUsefulCoin)
    .sort((a, b) => coinScore(b) - coinScore(a) || a.coin.localeCompare(b.coin));
}

function sortText(a, b) {
  return String(a || "").localeCompare(String(b || ""));
}

function AssistantVisualBoard({ visualSummary }) {
  const actions = visualSummary?.actions || {};
  const categories = visualSummary?.categories || {};
  const strengths = visualSummary?.strengths || {};
  const ranked = rankedCoins(visualSummary);
  const topCoins = ranked.slice(0, TOP_COINS);
  const extraCoins = ranked.length - topCoins.length;
  const pivotCoins = ranked.filter((coin) => coin.pivot === "on").slice(0, TOP_PIVOTS);

  const actionLabels = ["Buy", "Sell", "Hold"];
  const actionSeries = actionLabels.map((label) => num(actions[label]));
  const hasActions = actionSeries.some((value) => value > 0);

  const categoryLabels = Object.keys(categories).filter(
    (label) => label && label !== "Unknown" && num(categories[label]) > 0,
  );
  const categorySeries = categoryLabels.map((label) => num(categories[label]));

  const strengthLabels = ["1", "2", "3", "4", "5"];
  const strengthSeries = strengthLabels.map((label) => num(strengths[label]));
  const strengthBuckets = strengthSeries.filter((value) => value > 0).length;

  const coinRows = topCoins.map((coin) => ({
    coin: coin.coin,
    board: boardSide(coin) || "Hold",
    live: coin.liveLabel || coin.live || "Hold",
    strength: STRENGTH_LABELS[coin.strengthRank] || "—",
    pivot: coin.pivot,
  }));

  const sideClass = (value) => {
    const side = activeSide(value);
    if (side === "Buy") return styles.buy;
    if (side === "Sell") return styles.sell;
    return styles.hold;
  };

  const coinColumns = [
    {
      title: "Coin",
      dataIndex: "coin",
      sorter: (a, b) => sortText(a.coin, b.coin),
      render: (value) => (
        <span className={styles.coinChip}>
          <img
            src={`https://cryptocrispypro.s3.ap-southeast-2.amazonaws.com/symbol_icons/${value}.png`}
            alt=""
            className={styles.coinLogo}
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = "/placeholders/asset-logo.svg";
            }}
          />
          {value}
        </span>
      ),
    },
    {
      title: "Board",
      dataIndex: "board",
      sorter: (a, b) => sortText(a.board, b.board),
      render: (value) => <span className={sideClass(value)}>{value}</span>,
    },
    {
      title: `Live ${normalizeLiveTimeframe(visualSummary?.live_timeframe)}`,
      dataIndex: "live",
      sorter: (a, b) => sortText(a.live, b.live),
      render: (value) => <span className={sideClass(value)}>{value}</span>,
    },
    { title: "Strength", dataIndex: "strength", sorter: (a, b) => sortText(a.strength, b.strength) },
    {
      title: "Pivot",
      dataIndex: "pivot",
      sorter: (a, b) => sortText(a.pivot, b.pivot),
      render: (value) => <span className={value === "on" ? styles.pivot : styles.hold}>{value}</span>,
    },
  ];

  return (
    <div className={styles.visualBoard}>
      <div className={styles.chartGrid}>
        {hasActions && (
          <div className={styles.chartCard}>
            <h4>Buy / Sell / Hold</h4>
            <Chart
              type="donut"
              height={240}
              series={actionSeries}
              options={{
                labels: actionLabels,
                colors: ACTION_COLORS,
                legend: { position: "bottom" },
                dataLabels: {
                  formatter(val) {
                    return `${Number(val).toFixed(0)}%`;
                  },
                },
                plotOptions: {
                  pie: { donut: { size: "68%" } },
                },
              }}
            />
          </div>
        )}

        {categoryLabels.length > 0 && (
          <div className={styles.chartCard}>
            <h4>Categories</h4>
            <Chart
              type="bar"
              height={240}
              series={[{ name: "Signals", data: categorySeries }]}
              options={{
                chart: { toolbar: { show: false } },
                colors: [BAR_COLORS[0]],
                plotOptions: { bar: { borderRadius: 4, columnWidth: "45%" } },
                xaxis: { categories: categoryLabels },
                dataLabels: { enabled: false },
              }}
            />
          </div>
        )}

        {strengthBuckets > 1 && (
          <div className={styles.chartCard}>
            <h4>Strength</h4>
            <Chart
              type="bar"
              height={240}
              series={[{ name: "Count", data: strengthSeries }]}
              options={{
                chart: { toolbar: { show: false } },
                colors: [BAR_COLORS[1]],
                plotOptions: { bar: { borderRadius: 4, columnWidth: "45%" } },
                xaxis: {
                  categories: strengthLabels.map((n) => STRENGTH_LABELS[n] || n),
                },
                dataLabels: { enabled: false },
              }}
            />
          </div>
        )}
      </div>

      {coinRows.length > 0 && (
        <div className={styles.chartCard}>
          <h4>Top coins</h4>
          <Table
            size="small"
            pagination={false}
            rowKey={(row) => row.coin}
            columns={coinColumns}
            dataSource={coinRows}
            className={styles.summaryTable}
          />
          {extraCoins > 0 && <p className={styles.freshness}>and {extraCoins} more</p>}
        </div>
      )}

      {pivotCoins.length > 0 && (
        <div className={styles.pivotBlock}>
          <h4>Pivot evidence</h4>
          <ul className={styles.pivotList}>
            {pivotCoins.map((coin) => (
              <li key={coin.coin}>
                <span className={styles.coinChip}>
                  <img
                    src={`https://cryptocrispypro.s3.ap-southeast-2.amazonaws.com/symbol_icons/${coin.coin}.png`}
                    alt=""
                    className={styles.coinLogo}
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = "/placeholders/asset-logo.svg";
                    }}
                  />
                  {stockDisplayName(coin.coin)}
                </span>
                {` · ${normalizeLiveTimeframe(visualSummary?.live_timeframe)} `}
                {coin.live ? <span className={sideClass(coin.live)}>{coin.live}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default AssistantVisualBoard;
